import { FormattedMessage } from 'react-intl';
import { Badge, Box, Tooltip, Typography, Zoom } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useUserAccess } from '@ui-framework/providers/UserAccessProvider';
import useChatbot from './useChatbot';
import ChatWindow from './ChatWindow';
import { useChatbotPanel } from './ChatbotContext';

function ChatbotWidget({ suggestedQuestions }) {
  const { isOpen, open, close, viewMode, changeViewMode } = useChatbotPanel();
  const userAccess = useUserAccess();

  const {
    messages,
    isLoading,
    loadingSession,
    send,
    retry,
    groupName,
    setGroupName,
    unreadCount,
    sessions,
    sessionsLoading,
    refetchSessions,
    loadSession,
    startNewChat,
    handleFeedback,
    feedbackReason,
    setFeedbackReason,
    expandedNotHelpful,
    setExpandedNotHelpful,
    submittingFeedback,
  } = useChatbot({
    isOpen,
    open,
    close,
    userId: userAccess.id,
    companyType: userAccess.companyType,
  });

  return (
    <>
      {/* ── Launch pill — hidden while chat is open ── */}
      {/* exit fast (200ms) so it vanishes as the chat window expands from its corner; enter after a short delay (280ms) matching chat close animation */}
      <Zoom in={!isOpen} unmountOnExit timeout={{ enter: 220, exit: 180 }}>
        <Tooltip
          title={<FormattedMessage id="ASK_AIMEE" defaultMessage="Ask AIMee" />}
          placement="left">
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            sx={{
              position: 'fixed',
              bottom: 10,
              right: 10,
              zIndex: theme => theme.zIndex.drawer + 1,
            }}>
            <Box
              onClick={open}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && open()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                py: 1.5,
                borderRadius: '28px',
                maxWidth: 64,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #009ACD 0%, #0076a7 100%)',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(0,154,205,0.45)',
                transition:
                  'max-width 0.25s ease, box-shadow 0.2s ease, transform 0.2s ease',
                userSelect: 'none',
                '&:hover': {
                  maxWidth: 180,
                  boxShadow: '0 6px 28px rgba(0,154,205,0.6)',
                  transform: 'translateY(-2px)',
                  '& .MuiTypography-root': { opacity: 1 },
                },
                '&:active': { transform: 'translateY(0)' },
              }}>
              <AutoAwesomeIcon sx={{ fontSize: 20, flexShrink: 0 }} />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  letterSpacing: 0.2,
                  whiteSpace: 'nowrap',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                }}>
                <FormattedMessage
                  id="AI_ASSISTANT"
                  defaultMessage="AI Assistant"
                />
              </Typography>
            </Box>
          </Badge>
        </Tooltip>
      </Zoom>

      {/* Chat window handles its own enter/exit animation internally */}
      <ChatWindow
        isOpen={isOpen}
        viewMode={viewMode}
        onChangeViewMode={changeViewMode}
        onClose={close}
        messages={messages}
        isLoading={isLoading}
        onSend={send}
        onRetry={retry}
        groupName={groupName}
        onGroupChange={setGroupName}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        loadingSession={loadingSession}
        onRefetchSessions={refetchSessions}
        onLoadSession={loadSession}
        onStartNewChat={startNewChat}
        onFeedback={handleFeedback}
        feedbackReason={feedbackReason}
        setFeedbackReason={setFeedbackReason}
        expandedNotHelpful={expandedNotHelpful}
        setExpandedNotHelpful={setExpandedNotHelpful}
        submittingFeedback={submittingFeedback}
        suggestedQuestions={suggestedQuestions}
      />
    </>
  );
}

export default ChatbotWidget;
